import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import { useHealth, useMe, useThreads } from '../../hooks/queries'
import type { Thread } from '../../api/types'
import { initials, listTime, tripPeriod } from '../../lib/format'
import { usePreference } from '../../lib/preference'
import {
  ArchiveIcon,
  CheckIcon,
  ChevronDownIcon,
  DoubleCheckIcon,
  HiddenIcon,
  MuteIcon,
  PlusIcon,
  SettingsIcon,
  SmileyIcon,
} from '../../components/Icons'

/**
 * Die Chatliste.
 *
 * Für einen Empfänger steht hier je Reise ein Eintrag -- der Einzelchat mit
 * dem Reisenden. Für den Absender kommt unter jeder Reise je Empfänger ein
 * eingerückter Eintrag hinzu: dessen Antworten, die sonst niemand sieht.
 *
 * Und genau daran wächst die Liste: eine Reise mit zwölf Mitlesenden sind
 * dreizehn Zeilen. Deshalb lässt sich jede Reise einklappen, und die
 * abgeschlossenen Reisen stehen zusammengefasst am Ende.
 */
export function ChatList() {
  const threads = useThreads()
  const me = useMe()
  // Erst fragen, wenn etwas schiefgegangen ist -- im Normalfall braucht die
  // Liste diese Auskunft nicht.
  const health = useHealth(threads.isError)

  const [eingeklappt, setEingeklappt] = usePreference<number[]>('tb.chatlist.collapsed', [])
  const [archivOffen, setArchivOffen] = usePreference('tb.chatlist.archive-open', false)
  const [weggeraeumtOffen, setWeggeraeumtOffen] = usePreference(
    'tb.chatlist.hidden-open',
    false,
  )

  const gruppen = useMemo(() => gruppiere(threads.data ?? []), [threads.data])
  const laufende = gruppen.filter((gruppe) => !gruppe.archived)
  // Die beiden Stufen stehen getrennt, und das ist der ganze Punkt:
  // abgeschlossen heißt, es kommt nichts mehr dazu -- gelesen wird weiter.
  // Zurückgezogen heißt, niemand außer dir sieht die Reise überhaupt noch.
  // In einem gemeinsamen Abschnitt sah man den Unterschied nicht.
  const abgeschlossene = gruppen.filter((gruppe) => gruppe.archived && !gruppe.hidden)
  const zurueckgezogene = gruppen.filter((gruppe) => gruppe.hidden)

  function umschalten(tripId: number) {
    setEingeklappt(
      eingeklappt.includes(tripId)
        ? eingeklappt.filter((id) => id !== tripId)
        : [...eingeklappt, tripId],
    )
  }

  return (
    <>
      <header className="bar">
        <h1 className="bar__title bar__grow">Reisen</h1>
        {/* Wer nur mitliest, sieht den Knopf gar nicht erst. Die Regel steht
            im Server; hier wird sie nur nicht angeboten. */}
        {me.data?.may_create_trips && (
          <Link className="icon-button" to="/new" title="Neue Reise" aria-label="Neue Reise">
            <PlusIcon />
          </Link>
        )}
        <Link
          className="icon-button"
          to="/settings"
          title="Einstellungen"
          aria-label="Einstellungen"
        >
          <SettingsIcon />
        </Link>
      </header>

      <div className="threads">
        {threads.isPending && <p className="empty">Wird geladen …</p>}

        {threads.isError && (
          <div className="empty">
            <p className="empty__title">Die Liste ließ sich nicht laden</p>
            {/* Nach einer FTP-Auslieferung ist die häufigste Ursache eine
                Schemaänderung, die noch nicht eingespielt wurde. Dann steht
                hier, was genau fehlt -- statt dass man es im Protokoll des
                Servers suchen müsste. */}
            {(health.data?.schema_missing.length ?? 0) > 0 ? (
              <p>
                Der Datenbank fehlt, was diese Fassung erwartet:{' '}
                <code>{health.data?.schema_missing.join(', ')}</code>. Die
                Anweisung dazu steht in <code>docs/deployment-all-inkl.md</code>.
              </p>
            ) : (
              <p>{threads.error.message}</p>
            )}
          </div>
        )}

        {threads.data?.length === 0 &&
          (me.data?.may_create_trips ? (
            <div className="empty">
              <p className="empty__title">Noch keine Reise</p>
              <p>
                Lege eine Reise an und teile den Einladungslink mit denen, die
                mitlesen sollen.
              </p>
              <Link className="button" to="/new">
                Reise anlegen
              </Link>
            </div>
          ) : (
            <div className="empty">
              <p className="empty__title">Noch nichts zu lesen</p>
              <p>
                Sobald dich jemand zu einer Reise einlädt, erscheint sie hier.
              </p>
            </div>
          ))}

        {laufende.map((gruppe) => (
          <TripGroup
            key={gruppe.tripId}
            group={gruppe}
            collapsed={eingeklappt.includes(gruppe.tripId)}
            onToggle={() => umschalten(gruppe.tripId)}
          />
        ))}

        {/* Abgeschlossene Reisen bleiben lesbar, drängen sich aber nicht mehr
            zwischen die laufenden. Zugeklappt ist hier die Vorgabe: wer eine
            Reise abschließt, hat sie hinter sich. */}
        <Abschnitt
          titel="Abgeschlossen"
          symbol={<ArchiveIcon size={17} />}
          gruppen={abgeschlossene}
          offen={archivOffen}
          umschalten={() => setArchivOffen(!archivOffen)}
          eingeklappt={eingeklappt}
          reiseUmschalten={umschalten}
        />

        {/* Und darunter die zweite Stufe, getrennt: hier sieht die Reise
            niemand mehr außer dir. */}
        <Abschnitt
          titel="Nur für dich"
          symbol={<HiddenIcon size={17} />}
          gruppen={zurueckgezogene}
          offen={weggeraeumtOffen}
          umschalten={() => setWeggeraeumtOffen(!weggeraeumtOffen)}
          eingeklappt={eingeklappt}
          reiseUmschalten={umschalten}
        />
      </div>
    </>
  )
}

/**
 * Ein zusammengeklappter Abschnitt am Ende der Liste.
 *
 * Zweimal dasselbe Bauteil für die beiden Stufen, mit denen eine Reise zur
 * Ruhe kommt -- getrennt, weil sie Verschiedenes bedeuten: *Abgeschlossen*
 * heißt, es kommt nichts mehr dazu, gelesen wird weiter. *Nur für dich*
 * heißt, die Reise ist aus den Listen aller anderen verschwunden.
 */
function Abschnitt({
  titel,
  symbol,
  gruppen,
  offen,
  umschalten,
  eingeklappt,
  reiseUmschalten,
}: {
  titel: string
  symbol: ReactNode
  gruppen: TripThreads[]
  offen: boolean
  umschalten: () => void
  eingeklappt: number[]
  reiseUmschalten: (tripId: number) => void
}) {
  if (gruppen.length === 0) {
    return null
  }

  const ungelesen = ungeleseneVon(gruppen)

  return (
    <>
      <button
        type="button"
        className="threads__section"
        aria-expanded={offen}
        onClick={umschalten}
      >
        {symbol}
        <span className="threads__section-label">
          {titel} ({gruppen.length})
        </span>
        {/* Auch wenn in einer abgeschlossenen Reise nichts mehr dazukommt:
            was vorher ungelesen blieb, soll nicht hinter dem Abschnitt
            verschwinden. */}
        {ungelesen > 0 && !offen && (
          <span className="badge badge--muted">
            {ungelesen}
            <span className="visually-hidden"> ungelesene Nachrichten</span>
          </span>
        )}
        <ChevronDownIcon
          size={18}
          className={offen ? 'fold-arrow fold-arrow--open' : 'fold-arrow'}
        />
      </button>

      {offen &&
        gruppen.map((gruppe) => (
          <TripGroup
            key={gruppe.tripId}
            group={gruppe}
            collapsed={eingeklappt.includes(gruppe.tripId)}
            onToggle={() => reiseUmschalten(gruppe.tripId)}
          />
        ))}
    </>
  )
}

function ungeleseneVon(gruppen: TripThreads[]): number {
  return gruppen.reduce(
    (summe, gruppe) =>
      gruppe.children.reduce((zahl, kind) => zahl + kind.unread, summe + gruppe.head.unread),
    0,
  )
}

interface TripThreads {
  tripId: number
  head: Thread
  children: Thread[]
  archived: boolean
  hidden: boolean
}

/**
 * Aus der flachen Liste des Servers werden Reisen mit ihren Antwortverläufen.
 *
 * Die Reihenfolge kommt vom Server und bleibt, wie sie ist: dort entscheidet
 * die jüngste Regung einer Reise, und der Reise-Chat steht am Kopf seiner
 * Gruppe. Hier wird nur zusammengefasst, was zusammengehört.
 */
function gruppiere(threads: Thread[]): TripThreads[] {
  const gruppen: TripThreads[] = []

  for (const thread of threads) {
    const letzte = gruppen[gruppen.length - 1]
    if (thread.with_user === null) {
      gruppen.push({
        tripId: thread.trip_id,
        head: thread,
        children: [],
        archived: thread.archived,
        hidden: thread.hidden,
      })
    } else if (letzte && letzte.tripId === thread.trip_id) {
      letzte.children.push(thread)
    }
  }

  return gruppen
}

function TripGroup({
  group,
  collapsed,
  onToggle,
}: {
  group: TripThreads
  collapsed: boolean
  onToggle: () => void
}) {
  const location = useLocation()

  // Wer gerade in einem Antwortverlauf steht, soll ihn auch in der Liste
  // sehen -- eine Liste, die den offenen Chat wegklappt, verliert einen.
  const offenerChat = location.pathname.startsWith(`/t/${group.tripId}/`)
  const zu = collapsed && !offenerChat && group.children.length > 0

  // Zugeklappt steht die Zeile für die ganze Reise, also zählt sie auch für
  // die ganze Reise. Ohne das verstecken sich neue Antworten hinter einem
  // Pfeil, und das Einklappen kostet Nachrichten statt Zeilen.
  const ungelesen = zu
    ? group.children.reduce((summe, kind) => summe + kind.unread, group.head.unread)
    : group.head.unread

  return (
    <>
      <div className="thread-row">
        <ThreadRow thread={group.head} unread={ungelesen} />

        {group.children.length > 0 && (
          <button
            type="button"
            className="thread-fold"
            aria-expanded={!zu}
            title={zu ? 'Antwortverläufe zeigen' : 'Antwortverläufe einklappen'}
            aria-label={
              zu
                ? `Antwortverläufe von ${group.head.trip_title} zeigen`
                : `Antwortverläufe von ${group.head.trip_title} einklappen`
            }
            onClick={onToggle}
          >
            {/* Die Zahl steht am Knopf, nicht am Titel: im schmalen Fenster
                nähme sie sonst dem Reisenamen den Platz, und der Name wiegt
                schwerer als die Zahl. */}
            {zu && <span className="thread-fold__count">{group.children.length}</span>}
            <ChevronDownIcon
              size={20}
              className={zu ? 'fold-arrow' : 'fold-arrow fold-arrow--open'}
            />
          </button>
        )}
      </div>

      {!zu && group.children.map((kind) => (
        <PartnerRow key={`${kind.trip_id}:${kind.with_user?.id}`} thread={kind} />
      ))}
    </>
  )
}

/**
 * Ein Empfänger unter seiner Reise -- eine Zeile, mehr nicht.
 *
 * Bewusst schmaler als der Reise-Chat darüber: eine Reise mit zwölf
 * Mitlesenden sind dreizehn Zeilen, und wenn jede so hoch ist wie die Reise
 * selbst, sieht man vor lauter Empfängern die Reisen nicht mehr. Deshalb
 * ohne Bild und ohne Vorschautext -- der ist hier ohnehin fast immer die
 * eigene letzte Rundmail.
 *
 * Statt dessen zwei Zahlen: wie viel in diesem Verlauf steht und wie viel
 * davon neu ist. Beide kommen aus derselben Bedingung wie der Verlauf selbst.
 */
function PartnerRow({ thread }: { thread: Thread }) {
  const location = useLocation()
  const partner = thread.with_user
  const to = `/t/${thread.trip_id}/with/${partner?.id}`

  return (
    <NavLink
      to={to}
      className="partner-item"
      aria-current={location.pathname === to ? 'true' : undefined}
    >
      {/* Kursiv, weil stillgelegt kein Zustand der Reise ist, sondern des
          Kontos: der Verlauf bleibt, die Person kommt nicht mehr herein. */}
      <span className={partner?.disabled ? 'partner-item__name partner-item__name--still' : 'partner-item__name'}>
        {partner?.display_name}
      </span>

      {partner?.disabled && <span className="partner-item__note">stillgelegt</span>}
      {!partner?.disabled && partner?.left && (
        <span className="partner-item__note">nicht mehr dabei</span>
      )}
      {/* Erklärt das fehlende Häkchen: an ihn geht nichts mehr raus. */}
      {!partner?.disabled && !partner?.left && partner?.cut_off && (
        <span className="partner-item__note" title="Bekommt nichts Neues">
          pausiert
        </span>
      )}

      {/* Was von ihm kam: Beiträge und Zeichen getrennt. Eine Reaktion ist
          keine Wortmeldung -- und gerade der Unterschied ist die Auskunft.
          Die Null bleibt stehen statt zu verschwinden: „hat noch nie
          geschrieben" ist eine Aussage, eine leere Stelle wäre keine. */}
      <span className="partner-item__count" title={`${thread.replies} Beiträge von ihm`}>
        {thread.replies}
        <span className="visually-hidden">
          {thread.replies === 1 ? ' Beitrag geschrieben' : ' Beiträge geschrieben'}
        </span>
      </span>

      {thread.reactions > 0 && (
        <span className="partner-item__count" title={`${thread.reactions} Reaktionen`}>
          <SmileyIcon size={12} />
          {thread.reactions}
          <span className="visually-hidden"> Reaktionen</span>
        </span>
      )}

      {/* Zwei Haken heißt gelesen, einer heißt bereitgelegt -- und gar keiner
          heißt: hier wird nichts zugestellt. Eine leere Reise, jemand
          Pausierter, jemand, der nicht mehr dabei ist: ein Haken behauptete
          dort eine Zustellung, die es nicht gibt. */}
      {thread.read !== null && (
        <span
          className={thread.read ? 'partner-item__haken partner-item__haken--gelesen' : 'partner-item__haken'}
          title={thread.read ? 'Hat alles gelesen' : 'Liegt für ihn bereit, noch nicht gelesen'}
        >
          {thread.read ? <DoubleCheckIcon size={14} /> : <CheckIcon size={14} />}
          <span className="visually-hidden">
            {thread.read ? 'Hat alles gelesen' : 'Noch nicht gelesen'}
          </span>
        </span>
      )}

      {thread.unread > 0 ? (
        <span className={thread.muted ? 'badge badge--muted' : 'badge'}>
          {thread.unread}
          <span className="visually-hidden"> ungelesene Nachrichten</span>
        </span>
      ) : (
        <span className="partner-item__badge-platz" aria-hidden="true" />
      )}
    </NavLink>
  )
}

/**
 * Der Reise-Chat -- die Zeile, die für die ganze Reise steht.
 *
 * Für einen Mitlesenden ist das sein einziger Eintrag zu dieser Reise, für
 * den Absender der Kopf seiner Gruppe. Die Empfänger darunter stehen als
 * `PartnerRow` und sehen bewusst anders aus.
 */
function ThreadRow({ thread, unread }: { thread: Thread; unread?: number }) {
  const location = useLocation()
  const to = `/t/${thread.trip_id}`
  const name = thread.trip_title
  const zahl = unread ?? thread.unread
  const zeitraum = tripPeriod(thread.starts_on, thread.ends_on)

  return (
    <NavLink
      to={to}
      className="thread-item"
      aria-current={location.pathname === to ? 'true' : undefined}
    >
      <span className="avatar" aria-hidden="true">
        {initials(name)}
      </span>

      {/* Ohne Zusatz hinter dem Titel: der Abschnitt „Nur für dich" sagt es
          schon, und im schmalen Fenster nähme der Zusatz dem Reisenamen den
          Platz -- derselbe Grund, aus dem die Zahl der Verläufe am
          Einklappknopf steht und nicht am Titel. */}
      <span className="thread-item__name">{name}</span>
      <span className="thread-item__time">{listTime(thread.last_message_at)}</span>

      {/* Unter dem Titel steht der Reisezeitraum, nicht der letzte Beitrag.
          Eine Reise ist kein Gruppenchat: was zuletzt geschrieben wurde,
          steht einen Fingertipp weiter, wann die Reise war aber nirgends
          sonst. Ist kein Zeitraum eingetragen, tritt die Vorschau an seine
          Stelle -- eine leere Zeile wäre schlechter als beides. */}
      <span className="thread-item__preview">
        {zeitraum !== '' ? (
          zeitraum
        ) : (
          <>
            {thread.last_message_own && thread.last_message_preview ? 'Du: ' : ''}
            {thread.last_message_preview || 'Noch keine Nachricht'}
          </>
        )}
      </span>

      {zahl > 0 ? (
        <span className={thread.muted ? 'badge badge--muted' : 'badge'}>
          {zahl}
          <span className="visually-hidden">
            {thread.muted ? ' ungelesene Nachrichten, stumm' : ' ungelesene Nachrichten'}
          </span>
        </span>
      ) : (
        thread.muted && (
          <span className="thread-item__mute" title="Stummgeschaltet">
            <MuteIcon size={14} />
            <span className="visually-hidden">Stummgeschaltet</span>
          </span>
        )
      )}
    </NavLink>
  )
}
